import React from 'react';
import { useForm } from 'react-hook-form';
import { Globe, Settings, Search, AlertCircle, Lock, Crown, Info, ShieldCheck } from 'lucide-react';
import { ScanFormData } from '../types/index';

interface ScanFormProps {
  onSubmit: (data: ScanFormData) => void;
  isLoading: boolean;
  error?: string;
}

export const ScanForm: React.FC<ScanFormProps> = ({ onSubmit, isLoading, error }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<ScanFormData>({
    defaultValues: {
      url: '',
      includeImages: true,
      includeDynamicContent: true,
      wcag2A: true,
      wcag2AA: true,
      wcag2AAA: true,
      section508: true,
    }
  });

  const watchedUrl = watch('url');

  const validateUrl = (value: string) => {
    if (!value) return 'URL is required';

    let urlToTest = value.trim();
    if (!urlToTest.startsWith('http://') && !urlToTest.startsWith('https://')) {
      urlToTest = `https://${urlToTest}`;
    }

    try {
      new URL(urlToTest);
      return true;
    } catch {
      return 'Please enter a valid URL';
    }
  };

  const handleFormSubmit = (data: ScanFormData) => {
    let normalizedUrl = data.url.trim();
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = `https://${normalizedUrl}`;
    }
    onSubmit({ ...data, url: normalizedUrl });
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* URL Input */}
        <div>
          <label htmlFor="url" className="form-label flex items-center">
            <Globe className="w-4 h-4 mr-2" />
            Website URL
          </label>
          <div className="relative">
            <input
              id="url"
              type="url"
              placeholder="https://example.com"
              className={`form-input pl-10 ${errors.url ? 'border-error-500 ring-error-500' : ''}`}
              {...register('url', {
                validate: validateUrl,
                required: 'URL is required'
              })}
              aria-describedby={errors.url ? 'url-error' : 'url-help'}
              aria-invalid={!!errors.url}
            />
            <Globe className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          {errors.url && (
            <p id="url-error" role="alert" className="mt-1 text-sm text-error-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.url.message}
            </p>
          )}
          <p id="url-help" className="mt-1 text-sm text-gray-500">
            Enter the full URL of the website you want to scan for accessibility issues.
          </p>
        </div>

        {/* Scan Options */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-center mb-3">
            <Settings className="w-5 h-5 mr-2 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">Scan Options</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Content Analysis</h4>

              <div className="flex items-start">
                <input
                  id="includeImages"
                  type="checkbox"
                  className="form-checkbox mt-0.5"
                  {...register('includeImages')}
                />
                <div className="ml-3">
                  <label htmlFor="includeImages" className="text-sm text-gray-700 cursor-pointer flex items-center">
                    Analyze Images
                    <span className="ml-2 flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-tighter">
                      <Crown className="w-2.5 h-2.5 mr-0.5" />
                      Pro
                    </span>
                  </label>
                  <p className="text-xs text-gray-500">
                    Detailed analysis of alt text and decorative images
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  id="includeDynamicContent"
                  type="checkbox"
                  className="form-checkbox mt-0.5"
                  {...register('includeDynamicContent')}
                />
                <div className="ml-3">
                  <label htmlFor="includeDynamicContent" className="text-sm text-gray-700 cursor-pointer">
                    Include Dynamic Content
                  </label>
                  <p className="text-xs text-gray-500">
                    Wait for JavaScript content and interactive elements to load
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Compliance Standards</h4>

              <div className="flex items-start">
                <input
                  id="wcag2A"
                  type="checkbox"
                  className="form-checkbox mt-0.5"
                  {...register('wcag2A')}
                />
                <div className="ml-3">
                  <label htmlFor="wcag2A" className="text-sm text-gray-700 cursor-pointer">
                    WCAG 2.2 Level A
                  </label>
                  <p className="text-xs text-gray-500">Basic accessibility compliance</p>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  id="wcag2AA"
                  type="checkbox"
                  className="form-checkbox mt-0.5"
                  {...register('wcag2AA')}
                />
                <div className="ml-3">
                  <label htmlFor="wcag2AA" className="text-sm text-gray-700 cursor-pointer flex items-center">
                    WCAG 2.2 Level AA
                    <span className="ml-2 flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-tighter">
                      <Lock className="w-2.5 h-2.5 mr-0.5" />
                      Pro
                    </span>
                  </label>
                  <p className="text-xs text-gray-500">Standard accessibility compliance</p>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  id="wcag2AAA"
                  type="checkbox"
                  className="form-checkbox mt-0.5"
                  {...register('wcag2AAA')}
                />
                <div className="ml-3">
                  <label htmlFor="wcag2AAA" className="text-sm text-gray-700 cursor-pointer flex items-center">
                    WCAG 2.2 Level AAA
                    <span className="ml-2 flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-tighter">
                      <Lock className="w-2.5 h-2.5 mr-0.5" />
                      Pro
                    </span>
                  </label>
                  <p className="text-xs text-gray-500">Enhanced accessibility compliance</p>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  id="section508"
                  type="checkbox"
                  className="form-checkbox mt-0.5"
                  {...register('section508')}
                />
                <div className="ml-3">
                  <label htmlFor="section508" className="text-sm text-gray-700 cursor-pointer">
                    Section 508
                  </label>
                  <p className="text-xs text-gray-500">US federal accessibility standards</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 px-4 py-3 bg-primary-50/50 rounded-xl border border-primary-100 flex flex-col space-y-3">
            <div className="flex items-start">
              <ShieldCheck className="w-4 h-4 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-primary-900 font-medium">
                Your Privacy Matters. <span className="text-primary-700 font-normal">We don't save any data from your website — your scan remains secure and private.</span>
              </p>
            </div>
            <div className="flex items-start pt-2 border-t border-primary-100">
              <Info className="w-4 h-4 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-primary-800 leading-relaxed italic">
                Pro features (Level AA, AAA, and Image analysis) are included in the scan but detailed results require a one-time professional report fee.
              </p>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div role="alert" className="bg-error-50 border border-error-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-error-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-error-800">Scan Error</h3>
                <p className="mt-1 text-sm text-error-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={isLoading || !watchedUrl}
            className="btn-primary btn-lg flex items-center space-x-2 min-w-[200px]"
            aria-describedby="scan-button-help"
          >
            {isLoading ? (
              <>
                <div className="loading-spinner h-5 w-5" />
                <span>Scanning...</span>
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                <span>Scan Website</span>
              </>
            )}
          </button>
        </div>

        <p id="scan-button-help" className="text-center text-sm text-gray-500">
          {isLoading
            ? 'Please wait while we analyze the website accessibility...'
            : 'Click to start a comprehensive accessibility scan'
          }
        </p>
      </form>
    </div>
  );
};
